import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { AccountLayout } from '@/components/layout/AccountLayout';
import { ClientLayout } from '@/components/layout/ClientLayout';
import { AuthGuard } from '@/components/auth/AuthGuard';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { ErrorBoundary as UtilErrorBoundary } from '@/components/util/ErrorBoundary';
import { PageSkeleton } from '@/components/ui/Skeleton';
// Eager — most-visited pages
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterAgencyPage } from '@/pages/auth/RegisterAgencyPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { AccountsListPage } from '@/pages/accounts/AccountsListPage';
import { AccountDetailPage } from '@/pages/accounts/AccountDetailPage';

import { useBranding } from '@/hooks/useBranding';

// Polish pass — Cmd+K command palette mounted globally
import { CommandPalette } from '@/components/CommandPalette';
// pwa_ — first-run product tour
import { ProductTour } from '@/components/pwa/ProductTour';
// leg_ — cookie consent banner mounted app-wide
import { CookieConsentBanner } from '@/components/leg/CookieConsentBanner';
// comm_ — top-of-app announcement banner is mounted inside AppLayout

// ============================================================================
// Lazy route imports — code splitting for heavy / rarely-visited pages.
// Each becomes its own JS chunk to keep the main bundle small.
// ============================================================================
const NewAccountPage = lazy(() => import('@/pages/accounts/NewAccountPage').then(m => ({ default: m.NewAccountPage })));
const DiagnosticsPage = lazy(() => import('@/pages/DiagnosticsPage').then(m => ({ default: m.DiagnosticsPage })));
const MutationsPage = lazy(() => import('@/pages/MutationsPage').then(m => ({ default: m.MutationsPage })));
const ChatPage = lazy(() => import('@/pages/ChatPage').then(m => ({ default: m.ChatPage })));

const ExperimentsPage = lazy(() => import('@/pages/ExperimentsPage').then(m => ({ default: m.ExperimentsPage })));
const RecommendationsPage = lazy(() => import('@/pages/RecommendationsPage').then(m => ({ default: m.RecommendationsPage })));
const ConversionUploadsPage = lazy(() => import('@/pages/ConversionUploadsPage').then(m => ({ default: m.ConversionUploadsPage })));
const AudiencesPage = lazy(() => import('@/pages/AudiencesPage').then(m => ({ default: m.AudiencesPage })));
const ConversionValueRulesPage = lazy(() => import('@/pages/ConversionValueRulesPage').then(m => ({ default: m.ConversionValueRulesPage })));
const InsightsPage = lazy(() => import('@/pages/InsightsPage').then(m => ({ default: m.InsightsPage })));
const LifecycleGoalsPage = lazy(() => import('@/pages/LifecycleGoalsPage').then(m => ({ default: m.LifecycleGoalsPage })));
const BrandGuidelinesPage = lazy(() => import('@/pages/BrandGuidelinesPage').then(m => ({ default: m.BrandGuidelinesPage })));
const AccountLinksPage = lazy(() => import('@/pages/AccountLinksPage').then(m => ({ default: m.AccountLinksPage })));
const ScheduledReportsPage = lazy(() => import('@/pages/ScheduledReportsPage').then(m => ({ default: m.ScheduledReportsPage })));
const ConversionActionsPage = lazy(() => import('@/pages/ConversionActionsPage').then(m => ({ default: m.ConversionActionsPage })));
const ChangeEventsPage = lazy(() => import('@/pages/ChangeEventsPage').then(m => ({ default: m.ChangeEventsPage })));
const ReachForecastPage = lazy(() => import('@/pages/ReachForecastPage').then(m => ({ default: m.ReachForecastPage })));
const ReportsPage = lazy(() => import('@/pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const BillingPage = lazy(() => import('@/pages/billing/BillingPage').then(m => ({ default: m.BillingPage })));
const CampaignWizardPage = lazy(() => import('@/pages/CampaignWizardPage').then(m => ({ default: m.CampaignWizardPage })));
const FeedOptimizationPage = lazy(() => import('@/pages/FeedOptimizationPage').then(m => ({ default: m.FeedOptimizationPage })));
const AlertsPage = lazy(() => import('@/pages/AlertsPage').then(m => ({ default: m.AlertsPage })));

const AgencyOverviewPage = lazy(() => import('@/pages/agency/AgencyOverviewPage').then(m => ({ default: m.AgencyOverviewPage })));
const AgencyTeamPage = lazy(() => import('@/pages/agency/TeamPage').then(m => ({ default: m.AgencyTeamPage })));
// team_ — new team management suite
const TeamMainPage = lazy(() => import('@/pages/team/AgencyTeamPage').then(m => ({ default: m.AgencyTeamPage })));
const TeamSubAgenciesPage = lazy(() => import('@/pages/team/SubAgenciesPage').then(m => ({ default: m.SubAgenciesPage })));
const TeamPermissionsPage = lazy(() => import('@/pages/team/PermissionsPage').then(m => ({ default: m.PermissionsPage })));
const TeamAccountMigratePage = lazy(() => import('@/pages/team/AccountMigratePage').then(m => ({ default: m.AccountMigratePage })));
const TeamTimeZonePage = lazy(() => import('@/pages/team/TimeZonePage').then(m => ({ default: m.TimeZonePage })));
const AuditLogPage = lazy(() => import('@/pages/agency/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const CustomDomainsPage = lazy(() => import('@/pages/agency/CustomDomainsPage').then(m => ({ default: m.CustomDomainsPage })));
const AgencyMetricsPage = lazy(() => import('@/pages/agency/AgencyMetricsPage').then(m => ({ default: m.AgencyMetricsPage })));

// InsightsHubPage — biggest at ~1500 lines
const InsightsHubPage = lazy(() => import('@/pages/InsightsHubPage').then(m => ({ default: m.InsightsHubPage })));

// autoopt_
const AutoOptimizationDashboardPage = lazy(() => import('@/pages/autoopt/AutoOptimizationDashboardPage').then(m => ({ default: m.AutoOptimizationDashboardPage })));
const AutoOptimizationAccountPage = lazy(() => import('@/pages/autoopt/AutoOptimizationAccountPage').then(m => ({ default: m.AutoOptimizationAccountPage })));
const AttributionPage = lazy(() => import('@/pages/autoopt/AttributionPage').then(m => ({ default: m.AttributionPage })));
const AutoOptExperimentsPage = lazy(() => import('@/pages/autoopt/ExperimentsPage').then(m => ({ default: m.AutoOptExperimentsPage })));

// ops/
const OpsReportsPage = lazy(() => import('@/pages/ops/OpsReportsPage').then(m => ({ default: m.OpsReportsPage })));
const OpsScheduledReportsPage = lazy(() => import('@/pages/ops/OpsScheduledReportsPage').then(m => ({ default: m.OpsScheduledReportsPage })));
const OpsActivityPage = lazy(() => import('@/pages/ops/OpsActivityPage').then(m => ({ default: m.OpsActivityPage })));
const OpsCopilotPage = lazy(() => import('@/pages/ops/OpsCopilotPage').then(m => ({ default: m.OpsCopilotPage })));
const OpsSecurityPage = lazy(() => import('@/pages/ops/OpsSecurityPage').then(m => ({ default: m.OpsSecurityPage })));
const OpsMutationsPage = lazy(() => import('@/pages/ops/OpsMutationsPage').then(m => ({ default: m.OpsMutationsPage })));
const OpsFindingsPage = lazy(() => import('@/pages/ops/OpsFindingsPage').then(m => ({ default: m.OpsFindingsPage })));
const KnowledgeBasePage = lazy(() => import('@/pages/ops/KnowledgeBasePage').then(m => ({ default: m.KnowledgeBasePage })));
const NotificationPreferencesPage = lazy(() => import('@/pages/ops/NotificationPreferencesPage').then(m => ({ default: m.NotificationPreferencesPage })));
const SlaPoliciesPage = lazy(() => import('@/pages/ops/SlaPoliciesPage').then(m => ({ default: m.SlaPoliciesPage })));
const NewCampaignShellPage = lazy(() => import('@/pages/ops/NewCampaignShellPage').then(m => ({ default: m.NewCampaignShellPage })));

// client/
const ClientDashboardPage = lazy(() => import('@/pages/client/ClientDashboardPage').then(m => ({ default: m.ClientDashboardPage })));
const ClientReportsPage = lazy(() => import('@/pages/client/ClientReportsPage').then(m => ({ default: m.ClientReportsPage })));
const ClientProposalsPage = lazy(() => import('@/pages/client/ClientProposalsPage').then(m => ({ default: m.ClientProposalsPage })));
const ClientInboxPage = lazy(() => import('@/pages/client/ClientInboxPage').then(m => ({ default: m.ClientInboxPage })));
const ClientInvoicesPage = lazy(() => import('@/pages/client/ClientInvoicesPage').then(m => ({ default: m.ClientInvoicesPage })));
const ClientChatPage = lazy(() => import('@/pages/client/ClientChatPage').then(m => ({ default: m.ClientChatPage })));
const ClientSettingsPage = lazy(() => import('@/pages/client/ClientSettingsPage').then(m => ({ default: m.ClientSettingsPage })));

// onboarding/
const OnboardingLayout = lazy(() => import('@/pages/onboarding/OnboardingLayout').then(m => ({ default: m.OnboardingLayout })));
const Step1RegisterPage = lazy(() => import('@/pages/onboarding/Step1RegisterPage').then(m => ({ default: m.Step1RegisterPage })));
const Step2PlanPage = lazy(() => import('@/pages/onboarding/Step2PlanPage').then(m => ({ default: m.Step2PlanPage })));
const Step3BrandingPage = lazy(() => import('@/pages/onboarding/Step3BrandingPage').then(m => ({ default: m.Step3BrandingPage })));
const Step4OAuthPage = lazy(() => import('@/pages/onboarding/Step4OAuthPage').then(m => ({ default: m.Step4OAuthPage })));
const Step5ImportAccountsPage = lazy(() => import('@/pages/onboarding/Step5ImportAccountsPage').then(m => ({ default: m.Step5ImportAccountsPage })));
const Step6CompletePage = lazy(() => import('@/pages/onboarding/Step6CompletePage').then(m => ({ default: m.Step6CompletePage })));

// polish/
const KnowledgeBaseAdminPage = lazy(() => import('@/pages/polish/KnowledgeBaseAdminPage').then(m => ({ default: m.KnowledgeBaseAdminPage })));
const GdprPage = lazy(() => import('@/pages/polish/GdprPage').then(m => ({ default: m.GdprPage })));

// bulk/
const BulkSyncPage = lazy(() => import('@/pages/bulk/BulkSyncPage').then(m => ({ default: m.BulkSyncPage })));
const AccountTagsPage = lazy(() => import('@/pages/bulk/AccountTagsPage').then(m => ({ default: m.AccountTagsPage })));
const BulkSyncSchedulePage = lazy(() => import('@/pages/bulk/BulkSyncSchedulePage').then(m => ({ default: m.BulkSyncSchedulePage })));
const BulkMutationsPage = lazy(() => import('@/pages/bulk/BulkMutationsPage').then(m => ({ default: m.BulkMutationsPage })));

// rpt/
const CustomReportsPage = lazy(() => import('@/pages/rpt/CustomReportsPage').then(m => ({ default: m.CustomReportsPage })));
const CustomReportEditorPage = lazy(() => import('@/pages/rpt/CustomReportEditorPage').then(m => ({ default: m.CustomReportEditorPage })));
const CustomReportViewPage = lazy(() => import('@/pages/rpt/CustomReportViewPage').then(m => ({ default: m.CustomReportViewPage })));
const ActivityExportPage = lazy(() => import('@/pages/rpt/ActivityExportPage').then(m => ({ default: m.ActivityExportPage })));
const QuietHoursPage = lazy(() => import('@/pages/rpt/QuietHoursPage').then(m => ({ default: m.QuietHoursPage })));
const DigestPreviewPage = lazy(() => import('@/pages/rpt/DigestPreviewPage').then(m => ({ default: m.DigestPreviewPage })));
const EmailTemplatesPage = lazy(() => import('@/pages/rpt/EmailTemplatesPage').then(m => ({ default: m.EmailTemplatesPage })));
const PerformancePage = lazy(() => import('@/pages/rpt/PerformancePage').then(m => ({ default: m.PerformancePage })));

// adm/
const AdmJobQueuePage = lazy(() => import('@/pages/adm/JobQueuePage').then(m => ({ default: m.JobQueuePage })));
const AdmErrorLogPage = lazy(() => import('@/pages/adm/ErrorLogPage').then(m => ({ default: m.ErrorLogPage })));
const AdmDatabaseStatsPage = lazy(() => import('@/pages/adm/DatabaseStatsPage').then(m => ({ default: m.DatabaseStatsPage })));
const AdmFeatureFlagsPage = lazy(() => import('@/pages/adm/FeatureFlagsPage').then(m => ({ default: m.FeatureFlagsPage })));
const AdmPlansPage = lazy(() => import('@/pages/adm/PlansPage').then(m => ({ default: m.PlansPage })));
const AdmSlowQueriesPage = lazy(() => import('@/pages/adm/SlowQueriesPage').then(m => ({ default: m.SlowQueriesPage })));
const AdmBrandVoicePage = lazy(() => import('@/pages/adm/BrandVoicePage').then(m => ({ default: m.BrandVoicePage })));

// bk/ — backup / DR / audit retention
const BkBackupsPage = lazy(() => import('@/pages/bk/BackupsPage').then(m => ({ default: m.BackupsPage })));
const BkAuditRetentionPage = lazy(() => import('@/pages/bk/AuditRetentionPage').then(m => ({ default: m.AuditRetentionPage })));

// stubs (lightweight but lazy for symmetry)
const ForgotPasswordPage = lazy(() => import('@/pages/stubs').then(m => ({ default: m.ForgotPasswordPage })));
const SettingsPage = lazy(() => import('@/pages/stubs').then(m => ({ default: m.SettingsPage })));
const BrandingPage = lazy(() => import('@/pages/stubs').then(m => ({ default: m.BrandingPage })));

// intg/
const ApiKeysPage = lazy(() => import('@/pages/intg/ApiKeysPage').then(m => ({ default: m.ApiKeysPage })));
const IntgWebhooksPage = lazy(() => import('@/pages/intg/WebhooksPage').then(m => ({ default: m.WebhooksPage })));
const IncomingWebhooksPage = lazy(() => import('@/pages/intg/IncomingWebhooksPage').then(m => ({ default: m.IncomingWebhooksPage })));
const IntgSessionsPage = lazy(() => import('@/pages/intg/SessionsPage').then(m => ({ default: m.SessionsPage })));
const MagicLinkVerifyPage = lazy(() => import('@/pages/auth/MagicLinkVerifyPage').then(m => ({ default: m.MagicLinkVerifyPage })));

// bid/
const BiddingRulesPage = lazy(() => import('@/pages/bid/BiddingRulesPage').then(m => ({ default: m.BiddingRulesPage })));
const BiddingRuleEditorPage = lazy(() => import('@/pages/bid/BiddingRuleEditorPage').then(m => ({ default: m.BiddingRuleEditorPage })));
const DayPartingPage = lazy(() => import('@/pages/bid/DayPartingPage').then(m => ({ default: m.DayPartingPage })));
const SearchQueryMiningPage = lazy(() => import('@/pages/bid/SearchQueryMiningPage').then(m => ({ default: m.SearchQueryMiningPage })));
const NegativeKeywordListsPage = lazy(() => import('@/pages/bid/NegativeKeywordListsPage').then(m => ({ default: m.NegativeKeywordListsPage })));
const BidSimulatorPage = lazy(() => import('@/pages/bid/BidSimulatorPage').then(m => ({ default: m.BidSimulatorPage })));

// multi/
const MultiAccountPacingPage = lazy(() => import('@/pages/multi/MultiAccountPacingPage').then(m => ({ default: m.MultiAccountPacingPage })));
const MultiBenchmarksPage = lazy(() => import('@/pages/multi/BenchmarksPage').then(m => ({ default: m.BenchmarksPage })));
const IndustryBenchmarksPage = lazy(() => import('@/pages/multi/IndustryBenchmarksPage').then(m => ({ default: m.IndustryBenchmarksPage })));
const SpendAnomalyPage = lazy(() => import('@/pages/multi/SpendAnomalyPage').then(m => ({ default: m.SpendAnomalyPage })));
const ChangeCalendarPage = lazy(() => import('@/pages/multi/ChangeCalendarPage').then(m => ({ default: m.ChangeCalendarPage })));
const RollforwardPage = lazy(() => import('@/pages/multi/RollforwardPage').then(m => ({ default: m.RollforwardPage })));

// pwa/
const PwaSettingsPage = lazy(() => import('@/pages/pwa/PwaSettingsPage').then(m => ({ default: m.PwaSettingsPage })));

// comp/
const ComplianceCheckerPage = lazy(() => import('@/pages/comp/ComplianceCheckerPage').then(m => ({ default: m.ComplianceCheckerPage })));
const ComplianceRulesetsPage = lazy(() => import('@/pages/comp/ComplianceRulesetsPage').then(m => ({ default: m.ComplianceRulesetsPage })));
const PageSpeedPage = lazy(() => import('@/pages/comp/PageSpeedPage').then(m => ({ default: m.PageSpeedPage })));
const CompetitorMonitoringPage = lazy(() => import('@/pages/comp/CompetitorMonitoringPage').then(m => ({ default: m.CompetitorMonitoringPage })));
const LandingPageABTestPage = lazy(() => import('@/pages/comp/LandingPageABTestPage').then(m => ({ default: m.LandingPageABTestPage })));

// crv/
const AiImagesPage = lazy(() => import('@/pages/crv/AiImagesPage').then(m => ({ default: m.AiImagesPage })));
const DataStudioConnectorPage = lazy(() => import('@/pages/crv/DataStudioConnectorPage').then(m => ({ default: m.DataStudioConnectorPage })));
const ConversionPathPage = lazy(() => import('@/pages/crv/ConversionPathPage').then(m => ({ default: m.ConversionPathPage })));
const GeoHeatmapPage = lazy(() => import('@/pages/crv/GeoHeatmapPage').then(m => ({ default: m.GeoHeatmapPage })));
const DemographicsDeepPage = lazy(() => import('@/pages/crv/DemographicsDeepPage').then(m => ({ default: m.DemographicsDeepPage })));

// share/
const SharedBudgetsPage = lazy(() => import('@/pages/share/SharedBudgetsPage').then(m => ({ default: m.SharedBudgetsPage })));
const AutomationRulesPage = lazy(() => import('@/pages/share/AutomationRulesPage').then(m => ({ default: m.AutomationRulesPage })));
const AgencyBackupPage = lazy(() => import('@/pages/share/AgencyBackupPage').then(m => ({ default: m.AgencyBackupPage })));
const StatusPage = lazy(() => import('@/pages/share/StatusPage').then(m => ({ default: m.StatusPage })));

// help_ — changelog, roadmap, videos, feature voting, bug reports
const ChangelogPage = lazy(() => import('@/pages/help/ChangelogPage').then(m => ({ default: m.ChangelogPage })));
const RoadmapPage = lazy(() => import('@/pages/help/RoadmapPage').then(m => ({ default: m.RoadmapPage })));
const HelpVideosPage = lazy(() => import('@/pages/help/VideosPage').then(m => ({ default: m.VideosPage })));
const FeatureVotingPage = lazy(() => import('@/pages/help/FeatureVotingPage').then(m => ({ default: m.FeatureVotingPage })));
const BugReportsAdminPage = lazy(() => import('@/pages/help/BugReportsAdminPage').then(m => ({ default: m.BugReportsAdminPage })));

// edu_ — education hub: tutorials, best practices, glossary, demo
const EduTutorialsPage = lazy(() => import('@/pages/edu/TutorialsPage').then(m => ({ default: m.TutorialsPage })));
const EduTutorialDetailPage = lazy(() => import('@/pages/edu/TutorialDetailPage').then(m => ({ default: m.TutorialDetailPage })));
const EduBestPracticesPage = lazy(() => import('@/pages/edu/BestPracticesPage').then(m => ({ default: m.BestPracticesPage })));
const EduGlossaryPage = lazy(() => import('@/pages/edu/GlossaryPage').then(m => ({ default: m.GlossaryPage })));
const EduDemoSandboxPage = lazy(() => import('@/pages/edu/DemoSandboxPage').then(m => ({ default: m.DemoSandboxPage })));

// comm_ — communications hub: inbox, announcements, discussions, email aliases
const CommInboxPage = lazy(() => import('@/pages/comm/InboxPage').then(m => ({ default: m.InboxPage })));
const CommAnnouncementsAdminPage = lazy(() => import('@/pages/comm/AnnouncementsAdminPage').then(m => ({ default: m.AnnouncementsAdminPage })));
const CommAccountDiscussionsPage = lazy(() => import('@/pages/comm/AccountDiscussionsPage').then(m => ({ default: m.AccountDiscussionsPage })));
const CommEmailAliasesPage = lazy(() => import('@/pages/comm/EmailAliasesPage').then(m => ({ default: m.EmailAliasesPage })));

// pub_ — public API docs, embed builder, API usage, webhook events catalog
const ApiDocsPage = lazy(() => import('@/pages/pub/ApiDocsPage').then(m => ({ default: m.ApiDocsPage })));
const PublicReportPage = lazy(() => import('@/pages/pub/PublicReportPage').then(m => ({ default: m.PublicReportPage })));
const EmbedBuilderPage = lazy(() => import('@/pages/pub/EmbedBuilderPage').then(m => ({ default: m.EmbedBuilderPage })));
const ApiUsagePage = lazy(() => import('@/pages/pub/ApiUsagePage').then(m => ({ default: m.ApiUsagePage })));
const WebhookEventsCatalogPage = lazy(() => import('@/pages/pub/WebhookEventsCatalogPage').then(m => ({ default: m.WebhookEventsCatalogPage })));

// int2_ — calendar, Slack commands, webhook templates, integration recipes, embed builder
const Int2CalendarIntegrationPage = lazy(() => import('@/pages/int2/CalendarIntegrationPage').then(m => ({ default: m.CalendarIntegrationPage })));
const Int2SlackCommandsPage = lazy(() => import('@/pages/int2/SlackCommandsPage').then(m => ({ default: m.SlackCommandsPage })));
const Int2WebhookTemplatesPage = lazy(() => import('@/pages/int2/WebhookTemplatesPage').then(m => ({ default: m.WebhookTemplatesPage })));
const Int2RecipesPage = lazy(() => import('@/pages/int2/RecipesPage').then(m => ({ default: m.RecipesPage })));
const Int2EmbedBuilderEnhancedPage = lazy(() => import('@/pages/int2/EmbedBuilderEnhancedPage').then(m => ({ default: m.EmbedBuilderEnhancedPage })));

// leg_ — legal / compliance pages (privacy, terms, recovery codes, data retention)
const PrivacyPolicyPage = lazy(() => import('@/pages/leg/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const TermsOfServicePage = lazy(() => import('@/pages/leg/TermsOfServicePage').then(m => ({ default: m.TermsOfServicePage })));
const RecoveryCodesPage = lazy(() => import('@/pages/leg/RecoveryCodesPage').then(m => ({ default: m.RecoveryCodesPage })));
const DataRetentionPage = lazy(() => import('@/pages/leg/DataRetentionPage').then(m => ({ default: m.DataRetentionPage })));

// mon_ — admin monitoring dashboards
const MonSystemHealthPage = lazy(() => import('@/pages/mon/SystemHealthPage').then(m => ({ default: m.SystemHealthPage })));
const MonEmailDeliveryPage = lazy(() => import('@/pages/mon/EmailDeliveryPage').then(m => ({ default: m.EmailDeliveryPage })));
const MonWebhookHealthPage = lazy(() => import('@/pages/mon/WebhookHealthPage').then(m => ({ default: m.WebhookHealthPage })));
const MonJobHealthPage = lazy(() => import('@/pages/mon/JobHealthPage').then(m => ({ default: m.JobHealthPage })));
const MonCircuitBreakersPage = lazy(() => import('@/pages/mon/CircuitBreakersPage').then(m => ({ default: m.CircuitBreakersPage })));
const MonCrashReportsPage = lazy(() => import('@/pages/mon/CrashReportsPage').then(m => ({ default: m.CrashReportsPage })));
const MonSlowRequestsPage = lazy(() => import('@/pages/mon/SlowRequestsPage').then(m => ({ default: m.SlowRequestsPage })));

// edu_ — education hub
const TutorialsPage = lazy(() => import('@/pages/edu/TutorialsPage').then(m => ({ default: m.TutorialsPage })));
const TutorialDetailPage = lazy(() => import('@/pages/edu/TutorialDetailPage').then(m => ({ default: m.TutorialDetailPage })));
const BestPracticesPage = lazy(() => import('@/pages/edu/BestPracticesPage').then(m => ({ default: m.BestPracticesPage })));
const GlossaryPage = lazy(() => import('@/pages/edu/GlossaryPage').then(m => ({ default: m.GlossaryPage })));
const DemoSandboxPage = lazy(() => import('@/pages/edu/DemoSandboxPage').then(m => ({ default: m.DemoSandboxPage })));

// biz_ — business intelligence: revenue, usage, funnels, health, paywall, telemetry
const BizRevenueAnalyticsPage = lazy(() => import('@/pages/biz/RevenueAnalyticsPage').then(m => ({ default: m.RevenueAnalyticsPage })));
const BizUsageAnalyticsPage = lazy(() => import('@/pages/biz/UsageAnalyticsPage').then(m => ({ default: m.UsageAnalyticsPage })));
const BizFunnelAnalysisPage = lazy(() => import('@/pages/biz/FunnelAnalysisPage').then(m => ({ default: m.FunnelAnalysisPage })));
const BizCustomerHealthPage = lazy(() => import('@/pages/biz/CustomerHealthPage').then(m => ({ default: m.CustomerHealthPage })));
const BizPaywallStatsPage = lazy(() => import('@/pages/biz/PaywallStatsPage').then(m => ({ default: m.PaywallStatsPage })));
const BizTelemetrySettingsPage = lazy(() => import('@/pages/biz/TelemetrySettingsPage').then(m => ({ default: m.TelemetrySettingsPage })));

// ops_v2 — cron inspect, runbook, health webhooks, maintenance, retry policies
const CronSetupPage = lazy(() => import('@/pages/ops_v2/CronSetupPage').then(m => ({ default: m.CronSetupPage })));
const RunbookPage = lazy(() => import('@/pages/ops_v2/RunbookPage').then(m => ({ default: m.RunbookPage })));
const HealthWebhooksPage = lazy(() => import('@/pages/ops_v2/HealthWebhooksPage').then(m => ({ default: m.HealthWebhooksPage })));
const MaintenancePage = lazy(() => import('@/pages/ops_v2/MaintenancePage').then(m => ({ default: m.MaintenancePage })));
const RetryPoliciesPage = lazy(() => import('@/pages/ops_v2/RetryPoliciesPage').then(m => ({ default: m.RetryPoliciesPage })));


/**
 * Wrap lazy element in Suspense + util ErrorBoundary so chunk-load failures
 * and runtime errors are caught per-route instead of nuking the whole app.
 */
function L({ children }: { children: React.ReactNode }) {
  return (
    <UtilErrorBoundary>
      <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
    </UtilErrorBoundary>
  );
}

export default function App() {
  // Apply white-label branding (CSS vars + favicon + title) on app boot.
  useBranding();

  return (
    <ErrorBoundary>
      <UtilErrorBoundary>
        {/* Polish pass — Cmd+K command palette mounted globally */}
        <CommandPalette />
        {/* pwa_ — first-run product tour */}
        <ProductTour />
        {/* leg_ — cookie consent banner */}
        <CookieConsentBanner />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterAgencyPage />} />
          <Route path="/forgot-password" element={<L><ForgotPasswordPage /></L>} />

          {/* intg_ — passwordless magic-link landing (public) */}
          <Route path="/auth/magic-link/verify" element={<L><MagicLinkVerifyPage /></L>} />

          {/* Multi-step onboarding wizard — public, no AuthGuard */}
          <Route path="/onboarding" element={<L><OnboardingLayout /></L>}>
            <Route index element={<L><Step1RegisterPage /></L>} />
            <Route path="register" element={<L><Step1RegisterPage /></L>} />
            <Route path="plan" element={<L><Step2PlanPage /></L>} />
            <Route path="branding" element={<L><Step3BrandingPage /></L>} />
            <Route path="oauth" element={<L><Step4OAuthPage /></L>} />
            <Route path="accounts" element={<L><Step5ImportAccountsPage /></L>} />
            <Route path="complete" element={<L><Step6CompletePage /></L>} />
          </Route>

          <Route
            element={
              <AuthGuard role="agency">
                <AppLayout />
              </AuthGuard>
            }
          >
            <Route path="/" element={<DashboardPage />} />
            <Route path="/accounts" element={<AccountsListPage />} />
            <Route path="/accounts/new" element={<L><NewAccountPage /></L>} />

            {/* Shared account layout with header + tabs */}
            <Route path="/accounts/:id" element={<AccountLayout />}>
              <Route index element={<AccountDetailPage />} />
              <Route path="campaign-wizard" element={<L><CampaignWizardPage /></L>} />
              <Route path="diagnostics" element={<L><DiagnosticsPage /></L>} />
              <Route path="mutations" element={<L><MutationsPage /></L>} />
              <Route path="chat" element={<L><ChatPage /></L>} />
              <Route path="reports" element={<L><ReportsPage /></L>} />
              <Route path="feed" element={<L><FeedOptimizationPage /></L>} />
              <Route path="feed-optimizer" element={<L><FeedOptimizationPage /></L>} />
              <Route path="experiments" element={<L><ExperimentsPage /></L>} />
              <Route path="recommendations" element={<L><RecommendationsPage /></L>} />
              <Route path="conversion-uploads" element={<L><ConversionUploadsPage /></L>} />
              <Route path="audiences" element={<L><AudiencesPage /></L>} />
              <Route path="conversion-value-rules" element={<L><ConversionValueRulesPage /></L>} />
              <Route path="insights" element={<L><InsightsPage /></L>} />
              <Route path="lifecycle-goals" element={<L><LifecycleGoalsPage /></L>} />
              <Route path="brand-guidelines" element={<L><BrandGuidelinesPage /></L>} />
              <Route path="links" element={<L><AccountLinksPage /></L>} />
              <Route path="scheduled-reports" element={<L><ScheduledReportsPage /></L>} />
              <Route path="conversion-actions" element={<L><ConversionActionsPage /></L>} />
              <Route path="change-events" element={<L><ChangeEventsPage /></L>} />
              <Route path="reach-forecast" element={<L><ReachForecastPage /></L>} />
            </Route>

            {/* Alerty / Alerts inbox — P1.6 BDOS 2.0 */}
            <Route path="/alerts" element={<L><AlertsPage /></L>} />

            {/* Agency settings */}
            <Route path="/settings" element={<L><SettingsPage /></L>} />
            <Route path="/agency/overview" element={<L><AgencyOverviewPage /></L>} />
            <Route path="/agency/branding" element={<L><BrandingPage /></L>} />
            <Route path="/agency/team" element={<L><AgencyTeamPage /></L>} />
            {/* team_ — new team management routes */}
            <Route path="/team" element={<L><TeamMainPage /></L>} />
            <Route path="/team/sub-agencies" element={<L><TeamSubAgenciesPage /></L>} />
            <Route path="/team/permissions" element={<L><TeamPermissionsPage /></L>} />
            <Route path="/team/migrate-account" element={<L><TeamAccountMigratePage /></L>} />
            <Route path="/team/timezone" element={<L><TeamTimeZonePage /></L>} />
            <Route path="/agency/domains" element={<L><CustomDomainsPage /></L>} />
            <Route path="/agency/metrics" element={<L><AgencyMetricsPage /></L>} />
            <Route path="/insights" element={<L><InsightsHubPage /></L>} />
            <Route path="/billing" element={<L><BillingPage /></L>} />
            <Route path="/audit-log" element={<L><AuditLogPage /></L>} />

            {/* Ops hub — cross-account global pages */}
            <Route path="/reports" element={<L><OpsReportsPage /></L>} />
            <Route path="/reports/scheduled" element={<L><OpsScheduledReportsPage /></L>} />
            <Route path="/activity" element={<L><OpsActivityPage /></L>} />
            <Route path="/copilot" element={<L><OpsCopilotPage /></L>} />
            <Route path="/settings/security" element={<L><OpsSecurityPage /></L>} />

            {/* Ops hub — mutations/findings/knowledge/notifications/sla/new campaign */}
            <Route path="/mutations" element={<L><OpsMutationsPage /></L>} />
            <Route path="/findings" element={<L><OpsFindingsPage /></L>} />
            <Route path="/knowledge" element={<L><KnowledgeBasePage /></L>} />
            <Route path="/knowledge/admin" element={<L><KnowledgeBaseAdminPage /></L>} />
            <Route path="/settings/notifications" element={<L><NotificationPreferencesPage /></L>} />
            <Route path="/settings/sla" element={<L><SlaPoliciesPage /></L>} />
            <Route path="/settings/gdpr" element={<L><GdprPage /></L>} />
            <Route path="/campaigns/new" element={<L><NewCampaignShellPage /></L>} />

            {/* Auto-optimization (P1 autoopt_) */}
            <Route path="/autoopt" element={<L><AutoOptimizationDashboardPage /></L>} />
            <Route path="/autoopt/attribution" element={<L><AttributionPage /></L>} />
            <Route path="/autoopt/experiments" element={<L><AutoOptExperimentsPage /></L>} />
            <Route path="/autoopt/:accountId" element={<L><AutoOptimizationAccountPage /></L>} />

            {/* Bulk ops + productivity (bulk_) */}
            <Route path="/bulk/sync" element={<L><BulkSyncPage /></L>} />
            <Route path="/bulk/tags" element={<L><AccountTagsPage /></L>} />
            <Route path="/bulk/schedules" element={<L><BulkSyncSchedulePage /></L>} />
            <Route path="/bulk/mutations" element={<L><BulkMutationsPage /></L>} />

            {/* Reporting + analytics + email templates (rpt_) */}
            <Route path="/rpt/custom" element={<L><CustomReportsPage /></L>} />
            <Route path="/rpt/custom/:id" element={<L><CustomReportEditorPage /></L>} />
            <Route path="/rpt/custom/:id/view" element={<L><CustomReportViewPage /></L>} />
            <Route path="/rpt/activity-export" element={<L><ActivityExportPage /></L>} />
            <Route path="/rpt/quiet-hours" element={<L><QuietHoursPage /></L>} />
            <Route path="/rpt/digest" element={<L><DigestPreviewPage /></L>} />
            <Route path="/rpt/email-templates" element={<L><EmailTemplatesPage /></L>} />
            <Route path="/rpt/performance" element={<L><PerformancePage /></L>} />

            {/* Admin tooling (adm_) */}
            <Route path="/admin/jobs" element={<L><AdmJobQueuePage /></L>} />
            <Route path="/admin/errors" element={<L><AdmErrorLogPage /></L>} />
            <Route path="/admin/db" element={<L><AdmDatabaseStatsPage /></L>} />
            <Route path="/admin/feature-flags" element={<L><AdmFeatureFlagsPage /></L>} />
            <Route path="/admin/plans" element={<L><AdmPlansPage /></L>} />
            <Route path="/admin/slow-queries" element={<L><AdmSlowQueriesPage /></L>} />
            <Route path="/admin/brand-voice/:account_id" element={<L><AdmBrandVoicePage /></L>} />

            {/* Backups / DR / audit retention (bk_) */}
            <Route path="/admin/backups" element={<L><BkBackupsPage /></L>} />
            <Route path="/admin/audit-retention" element={<L><BkAuditRetentionPage /></L>} />

            {/* Integrations (intg_) */}
            <Route path="/intg/api-keys" element={<L><ApiKeysPage /></L>} />
            <Route path="/intg/webhooks" element={<L><IntgWebhooksPage /></L>} />
            <Route path="/intg/incoming-webhooks" element={<L><IncomingWebhooksPage /></L>} />
            <Route path="/intg/sessions" element={<L><IntgSessionsPage /></L>} />

            {/* Bidding & Optimization (bid_) */}
            <Route path="/bid/rules" element={<L><BiddingRulesPage /></L>} />
            <Route path="/bid/rules/:id" element={<L><BiddingRuleEditorPage /></L>} />
            <Route path="/bid/day-parting" element={<L><DayPartingPage /></L>} />
            <Route path="/bid/search-query-mining" element={<L><SearchQueryMiningPage /></L>} />
            <Route path="/bid/negative-lists" element={<L><NegativeKeywordListsPage /></L>} />
            <Route path="/bid/simulator" element={<L><BidSimulatorPage /></L>} />

            {/* Cross-account analytics (multi_) */}
            <Route path="/multi/pacing" element={<L><MultiAccountPacingPage /></L>} />
            <Route path="/multi/benchmarks" element={<L><MultiBenchmarksPage /></L>} />
            <Route path="/multi/industry-benchmarks" element={<L><IndustryBenchmarksPage /></L>} />
            <Route path="/multi/spend-anomaly" element={<L><SpendAnomalyPage /></L>} />
            <Route path="/multi/calendar" element={<L><ChangeCalendarPage /></L>} />
            <Route path="/multi/rollforward" element={<L><RollforwardPage /></L>} />

            {/* PWA shell (pwa_) */}
            <Route path="/pwa/settings" element={<L><PwaSettingsPage /></L>} />

            {/* Compliance, Page Speed, Competitors, Landing A/B (comp_) */}
            <Route path="/comp/checker" element={<L><ComplianceCheckerPage /></L>} />
            <Route path="/comp/rulesets" element={<L><ComplianceRulesetsPage /></L>} />
            <Route path="/comp/page-speed" element={<L><PageSpeedPage /></L>} />
            <Route path="/comp/competitors" element={<L><CompetitorMonitoringPage /></L>} />
            <Route path="/comp/landing-ab" element={<L><LandingPageABTestPage /></L>} />

            {/* Creative tools (crv_) */}
            <Route path="/crv/ai-images" element={<L><AiImagesPage /></L>} />
            <Route path="/crv/datastudio" element={<L><DataStudioConnectorPage /></L>} />
            <Route path="/crv/conversion-paths" element={<L><ConversionPathPage /></L>} />
            <Route path="/crv/geo-heatmap" element={<L><GeoHeatmapPage /></L>} />
            <Route path="/crv/demographics" element={<L><DemographicsDeepPage /></L>} />

            {/* share_ : shared budgets, automation, backup */}
            <Route path="/share/budgets" element={<L><SharedBudgetsPage /></L>} />
            <Route path="/share/automation" element={<L><AutomationRulesPage /></L>} />
            <Route path="/share/backup" element={<L><AgencyBackupPage /></L>} />
            <Route path="/status" element={<L><StatusPage /></L>} />

            {/* edu_ : education hub — tutorials, best practices, glossary, demo */}
            <Route path="/edu/tutorials" element={<L><EduTutorialsPage /></L>} />
            <Route path="/edu/tutorials/:slug" element={<L><EduTutorialDetailPage /></L>} />
            <Route path="/edu/best-practices" element={<L><EduBestPracticesPage /></L>} />
            <Route path="/edu/glossary" element={<L><EduGlossaryPage /></L>} />
            <Route path="/edu/demo" element={<L><EduDemoSandboxPage /></L>} />

            {/* help_ : changelog, roadmap, videos, feature voting, bug reports */}
            <Route path="/help/changelog" element={<L><ChangelogPage /></L>} />
            <Route path="/help/roadmap" element={<L><RoadmapPage /></L>} />
            <Route path="/help/videos" element={<L><HelpVideosPage /></L>} />
            <Route path="/help/features" element={<L><FeatureVotingPage /></L>} />
            <Route path="/help/bug-reports" element={<L><BugReportsAdminPage /></L>} />

            {/* comm_ : communications hub */}
            <Route path="/comm/inbox" element={<L><CommInboxPage /></L>} />
            <Route path="/comm/announcements" element={<L><CommAnnouncementsAdminPage /></L>} />
            <Route path="/comm/email-aliases" element={<L><CommEmailAliasesPage /></L>} />
            <Route path="/comm/accounts/:id/discussions" element={<L><CommAccountDiscussionsPage /></L>} />

            {/* pub_ : public API docs, embed builder, API usage, webhook events */}
            <Route path="/api-docs" element={<L><ApiDocsPage /></L>} />
            <Route path="/public/reports/:slug" element={<L><PublicReportPage /></L>} />
            <Route path="/embed/builder" element={<L><EmbedBuilderPage /></L>} />
            <Route path="/intg/api-usage" element={<L><ApiUsagePage /></L>} />
            <Route path="/intg/webhook-events" element={<L><WebhookEventsCatalogPage /></L>} />

            {/* int2_ : calendar, Slack commands, webhook templates, recipes, embed builder */}
            <Route path="/int2/calendar" element={<L><Int2CalendarIntegrationPage /></L>} />
            <Route path="/int2/slack" element={<L><Int2SlackCommandsPage /></L>} />
            <Route path="/int2/webhook-templates" element={<L><Int2WebhookTemplatesPage /></L>} />
            <Route path="/int2/recipes" element={<L><Int2RecipesPage /></L>} />
            <Route path="/int2/embed-builder" element={<L><Int2EmbedBuilderEnhancedPage /></L>} />

            {/* leg_ — legal / compliance */}
            <Route path="/privacy" element={<L><PrivacyPolicyPage /></L>} />
            <Route path="/terms" element={<L><TermsOfServicePage /></L>} />
            <Route path="/settings/recovery-codes" element={<L><RecoveryCodesPage /></L>} />
            <Route path="/settings/data-retention" element={<L><DataRetentionPage /></L>} />

            {/* mon_ — admin monitoring dashboards */}
            <Route path="/admin/mon/system-health" element={<L><MonSystemHealthPage /></L>} />
            <Route path="/admin/mon/email-delivery" element={<L><MonEmailDeliveryPage /></L>} />
            <Route path="/admin/mon/webhooks" element={<L><MonWebhookHealthPage /></L>} />
            <Route path="/admin/mon/job-health" element={<L><MonJobHealthPage /></L>} />
            <Route path="/admin/mon/circuit-breakers" element={<L><MonCircuitBreakersPage /></L>} />
            <Route path="/admin/mon/errors" element={<L><MonCrashReportsPage /></L>} />
            <Route path="/admin/mon/slow-requests" element={<L><MonSlowRequestsPage /></L>} />

            {/* edu_ : tutorials, best practices, glossary, demo */}
            <Route path="/edu/tutorials" element={<L><TutorialsPage /></L>} />
            <Route path="/edu/tutorials/:slug" element={<L><TutorialDetailPage /></L>} />
            <Route path="/edu/best-practices" element={<L><BestPracticesPage /></L>} />
            <Route path="/edu/glossary" element={<L><GlossaryPage /></L>} />
            <Route path="/edu/demo" element={<L><DemoSandboxPage /></L>} />

            {/* biz_ : revenue, usage, funnels, customer health, paywall, telemetry */}
            <Route path="/admin/biz/revenue" element={<L><BizRevenueAnalyticsPage /></L>} />
            <Route path="/admin/biz/usage" element={<L><BizUsageAnalyticsPage /></L>} />
            <Route path="/admin/biz/funnels" element={<L><BizFunnelAnalysisPage /></L>} />
            <Route path="/admin/biz/customer-health" element={<L><BizCustomerHealthPage /></L>} />
            <Route path="/admin/biz/paywall-hits" element={<L><BizPaywallStatsPage /></L>} />
            <Route path="/settings/telemetry" element={<L><BizTelemetrySettingsPage /></L>} />

            {/* ops_v2 : cron-setup, runbook, health-webhooks, maintenance, retry-policies */}
            <Route path="/admin/cron-setup" element={<L><CronSetupPage /></L>} />
            <Route path="/admin/runbook" element={<L><RunbookPage /></L>} />
            <Route path="/admin/health-webhooks" element={<L><HealthWebhooksPage /></L>} />
            <Route path="/admin/maintenance" element={<L><MaintenancePage /></L>} />
            <Route path="/admin/retry-policies" element={<L><RetryPoliciesPage /></L>} />
          </Route>

          <Route
            path="/client"
            element={
              <AuthGuard role="client">
                <ClientLayout />
              </AuthGuard>
            }
          >
            <Route index element={<L><ClientDashboardPage /></L>} />
            <Route path="reports" element={<L><ClientReportsPage /></L>} />
            <Route path="proposals" element={<L><ClientProposalsPage /></L>} />
            <Route path="inbox" element={<L><ClientInboxPage /></L>} />
            <Route path="invoices" element={<L><ClientInvoicesPage /></L>} />
            <Route path="chat" element={<L><ClientChatPage /></L>} />
            <Route path="settings" element={<L><ClientSettingsPage /></L>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </UtilErrorBoundary>
    </ErrorBoundary>
  );
}
